import { useRef, useState } from "react";
import Button from "../components/ui/Button";
import ProgressScreen from "../components/ProgressScreen";
import { uploadProducts } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function UploaderPage() {
  const [step, setStep] = useState("select"); // select | uploading | success
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // File-upload phase: a real % of bytes sent.
  const [uploadPercent, setUploadPercent] = useState(0);
  // Processing phase: count of products done vs total, once the CSV
  // itself has finished uploading and Cloud Tasks starts working through it.
  const [phase, setPhase] = useState("uploading_file"); // "uploading_file" | "processing"
  const [productsDone, setProductsDone] = useState(0);
  const [productsTotal, setProductsTotal] = useState(0);

  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  function handleFileChange(selected) {
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith(".csv")) {
      setUploadError("Please choose a .csv file.");
      return;
    }
    setUploadError("");
    setFile(selected);
  }

  async function handleUpload() {
    if (!file) return;
    setUploadError("");
    setUploadPercent(0);
    setPhase("uploading_file");
    setProductsDone(0);
    setProductsTotal(0);
    setStep("uploading");

    try {
      const final = await uploadProducts(file, (event) => {
        if (event.type === "uploading_file") {
          setUploadPercent(event.progress);
        } else if (event.type === "batch_status" || event.type === "complete") {
          setPhase("processing");
          setProductsTotal(event.total);
          setProductsDone(Object.keys(event.results || {}).length);
        }
      });
      setResult(final);
      setStep("success");
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate("/login", { replace: true });
      } else {
        setUploadError(err.message || "Upload failed. Please try again.");
        setStep("select");
      }
    }
  }

  function resetUpload() {
    setFile(null);
    setResult(null);
    setUploadError("");
    setStep("select");
  }

  const progress = phase === "uploading_file"
    ? uploadPercent
    : productsTotal > 0
      ? Math.round((productsDone / productsTotal) * 100)
      : 0;

  const progressMessage = phase === "uploading_file"
    ? "Uploading file — % reflects file transfer only"
    : `Processing products — ${productsDone} of ${productsTotal} complete`;

  return (
    <>
      {step === "select" && (
        <div className="w-full max-w-xl flex flex-col gap-8">
          <div>
            <h2 className="font-display text-4xl font-bold tracking-tight">Upload Products</h2>
            <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray mt-2">
              CSV file, matching your Shopify product template
            </p>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              handleFileChange(e.dataTransfer.files?.[0]);
            }}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
            }}
            className={`cursor-pointer border-2 border-dashed rounded-none px-6 py-16 flex flex-col items-center justify-center gap-3 transition-colors duration-200 focus:outline-none focus-visible:border-collectors-black ${
              isDragging ? "border-collectors-black bg-white" : "border-collectors-light"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFileChange(e.target.files?.[0])}
            />
            {file ? (
              <>
                <p className="font-display text-xl font-medium">{file.name}</p>
                <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray">
                  {(file.size / 1024).toFixed(1)} KB — click to replace
                </p>
              </>
            ) : (
              <>
                <p className="font-display text-xl font-medium">Drop CSV here</p>
                <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray">
                  or click to browse
                </p>
              </>
            )}
          </div>

          {uploadError && (
            <p className="font-mono text-xs uppercase tracking-widest text-red-600">{uploadError}</p>
          )}

          <Button onClick={handleUpload} disabled={!file}>
            Upload Products
          </Button>
        </div>
      )}

      {step === "success" && (
        <div className="w-full max-w-xl flex flex-col gap-8">
          <div>
            <h2 className="font-display text-4xl font-bold tracking-tight">Upload Complete</h2>
            <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray mt-2">
              {file?.name}
            </p>
          </div>

          <div className="border border-solid border-collectors-black px-6 py-8 flex flex-col gap-4">
            {result?.created !== undefined && (
              <div className="flex justify-between font-mono text-sm uppercase tracking-widest">
                <span>Created</span>
                <span>{result.created}</span>
              </div>
            )}
            {result?.skipped !== undefined && (
              <div className="flex justify-between font-mono text-sm uppercase tracking-widest">
                <span>Skipped</span>
                <span>{result.skipped}</span>
              </div>
            )}
            {result?.created === undefined && result?.skipped === undefined && (
              <p className="font-mono text-sm uppercase tracking-widest">Products uploaded successfully.</p>
            )}
          </div>

          <Button variant="secondary" onClick={resetUpload}>
            Upload Another File
          </Button>
        </div>
      )}
      
      {step === "uploading" && (
        <ProgressScreen
          mode={phase === "uploading_file" ? "percent" : "count"}
          progress={uploadPercent}
          count={productsDone}
          total={productsTotal}
          message={progressMessage}
        />
      )}
    </>
  );
}